import React from 'react';
import {
  Pane,
  Card,
  Badge,
  Text,
  Heading,
  IconButton,
  Strong,
  Spinner,
  Button,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowRightIcon,
  PlayIcon,
  TickIcon,
  TrashIcon
} from 'evergreen-ui';
import { CommentSection } from '../../Comments';
import { getSeverityBackgroundColor } from '../utils';

export const HostsTab = ({
  hosts,
  isLoading,
  hasMore,
  searchQuery,
  commentCounts,
  expandedComments,
  manuallyCollapsedComments,
  expandedHosts,
  toggleCommentsExpansion,
  collapseComments,
  toggleHostExpansion,
  actionLoading,
  handleAction,
  openEntityOverview,
  currentProject,
  currentUser
}) => {
  const sortedHosts = [...hosts].sort((a, b) => {
    const aHasPorts = a.ports && a.ports.filter(port => port.state === 'open').length > 0;
    const bHasPorts = b.ports && b.ports.filter(port => port.state === 'open').length > 0;
    if (aHasPorts && !bHasPorts) return -1;
    if (!aHasPorts && bHasPorts) return 1;
    return 0;
  });

  return (
    <Pane>
      <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={16}>
        <Heading size={600}>
          Hosts {sortedHosts.length > 0 && `(${sortedHosts.length} shown${!searchQuery && hasMore ? ', more available' : ''})`}
        </Heading>
      </Pane>
      
      {sortedHosts.length === 0 && !isLoading ? (
        <Card background="tint1" padding={32} textAlign="center">
          <Text color="muted">
            {searchQuery ? 'No hosts match your search' : 'No hosts found'}
          </Text>
        </Card>
      ) : (
        <Pane>
          {sortedHosts.map(host => {
            const hasOpenPorts = host.ports && host.ports.filter(port => port.state === 'open').length > 0;
            
            return (
              <React.Fragment key={host.id}>
                <Card
                  background={host.is_new ? "#E6F4EA" : (host.is_checked ? "#F7F8FA" : "white")}
                  border={host.is_new ? "2px solid #47B881" : "1px solid #E6E8F0"}
                  padding={16}
                  marginBottom={8}
                  elevation={0}
                >
                  <Pane display="flex" alignItems="center" justifyContent="space-between">
                    <Pane flex={1}>
                      <Pane 
                        display="flex" 
                        alignItems="center" 
                        cursor={hasOpenPorts ? "pointer" : "default"}
                        onClick={hasOpenPorts ? () => toggleHostExpansion(host.id) : undefined}
                      >
                        {hasOpenPorts && (
                          <IconButton
                            icon={expandedHosts[host.id] ? ChevronDownIcon : ChevronRightIcon}
                            marginRight={8}
                            height={24}
                          />
                        )}
                        <Strong size={500} marginRight={8}>{host.ip_address}</Strong>
                        <Badge
                          backgroundColor={getSeverityBackgroundColor(host.severity)}
                          color={host.severity === 'critical' ? 'red' : 'default'}
                          marginRight={8}
                        >
                          {host.severity || 'none'}
                        </Badge>
                        {host.is_new && (
                          <Badge backgroundColor="#47B881" color="white" marginRight={8}>
                            NEW
                          </Badge>
                        )}
                        {host.is_checked && (
                          <Badge backgroundColor="#999999" color="white" marginRight={8}>
                            CHECKED
                          </Badge>
                        )}
                        {host.os && (
                          <Text size={300} color="muted" marginLeft={8}>
                            OS: {host.os}
                          </Text>
                        )}
                        {hasOpenPorts && (
                          <Badge backgroundColor="#E6F4EA" color="green" marginLeft={8}>
                            {host.ports.filter(port => port.state === 'open').length} ports
                          </Badge>
                        )}
                      </Pane>
                    </Pane>
                  
                    <Pane display="flex" gap={8}>
                      <Button
                        size="small"
                        appearance="minimal"
                        iconBefore={ArrowRightIcon}
                        onClick={() => openEntityOverview('host', host.id, { ip_address: host.ip_address, type: 'host' })}
                      >
                        View Details
                      </Button>
                      <Button
                        size="small"
                        appearance="minimal"
                        iconBefore={expandedComments[`host_id_${host.id}`] ? ChevronDownIcon : ChevronRightIcon}
                        onClick={() => toggleCommentsExpansion(`host_id_${host.id}`)}
                      >
                        Comments
                        {commentCounts[`host_id_${host.id}`] > 0 && (
                          <Badge marginLeft={8} backgroundColor="#1070CA" color="white">
                            {commentCounts[`host_id_${host.id}`]}
                          </Badge>
                        )}
                      </Button>
                      <Button
                        size="small"
                        iconBefore={PlayIcon}
                        isLoading={actionLoading[`nmap_host_${host.id}`]}
                        onClick={() => handleAction('nmap', 'host', host.id, {
                          ip: host.ip_address
                        })}
                      >
                        Run Nmap
                      </Button>
                      <Button
                        size="small"
                        iconBefore={TickIcon}
                        intent={host.is_checked ? "success" : "default"}
                        isLoading={actionLoading[`mark_checked_host_${host.id}`]}
                        onClick={() => handleAction('mark_checked', 'host', host.id, {
                          ip: host.ip_address
                        })}
                      >
                        {host.is_checked ? 'Uncheck' : 'Mark Checked'}
                      </Button>
                      <IconButton
                        icon={TrashIcon}
                        intent="danger"
                        size="small"
                        appearance="minimal"
                        isLoading={actionLoading[`delete_host_${host.id}`]}
                        onClick={() => {
                          if (window.confirm(`Delete host ${host.ip_address} and all associated data?`)) {
                            handleAction('delete', 'host', host.id);
                          }
                        }}
                      />
                    </Pane>
                  </Pane>
                
                  {expandedHosts[host.id] && hasOpenPorts && (
                    <Pane marginTop={16} marginLeft={32}>
                      <Heading size={400} marginBottom={12}>Open Ports</Heading>
                      <Pane display="flex" flexDirection="column" gap={8}>
                        {host.ports.filter(port => port.state === 'open').map(port => (
                          <Card key={port.id} background="tint1" padding={12}>
                            <Pane display="flex" alignItems="center" justifyContent="space-between">
                              <Pane>
                                <Text size={400} marginRight={8}>
                                  {port.number}/{port.protocol}
                                </Text>
                                {port.service && (
                                  <Text size={300} color="muted">
                                    Service: {port.service}
                                  </Text>
                                )}
                              </Pane>
                              <Pane display="flex" alignItems="center" gap={8}>
                                {port.is_new && (
                                  <Badge backgroundColor="#47B881" color="white">
                                    NEW
                                  </Badge>
                                )}
                                <Badge backgroundColor="#E6F4EA" color="green">
                                  OPEN
                                </Badge>
                              </Pane>
                            </Pane>
                          </Card>
                        ))}
                      </Pane>
                    </Pane>
                  )}
                </Card>
              
                <CommentSection
                  projectId={currentProject.id}
                  entityType="host_id"
                  entityId={host.id}
                  currentUserId={currentUser?.id}
                  compact={true}
                  forceShow={expandedComments[`host_id_${host.id}`] || false}
                  manuallyCollapsed={manuallyCollapsedComments[`host_id_${host.id}`] || false}
                  onAutoExpand={(entityId) => {
                    toggleCommentsExpansion(entityId);
                  }}
                  onCollapse={collapseComments}
                />
              </React.Fragment>
            );
          })}
          
          {isLoading && (
            <Pane display="flex" justifyContent="center" padding={16}>
              <Spinner />
            </Pane>
          )}
        </Pane>
      )}
    </Pane>
  );
};

